import { v4 as uuid } from 'uuid';

import { Bot, Channel } from '@/domain/bot';
import { BotMappieVersion } from '@/domain/bot-mappie';
import { VoiceConfig } from '@/domain/omnichannel-config';
import { isChatBot } from '@/utils/bot/bot-type-utils';
import {
    modulesToEdgesReactFlow,
    modulesToNodesReactFlow,
} from 'utils/reactflow/modules-to-graph-data';

/**
 * Transforms bot from format used in database (mappie) to format used at frontend
 * @param botMappie - bot specification in format used in mappie
 * @returns botFrontend -  bot specification in format used at frontend
 */

export const transformBotMappieForPortal = (
    botMappieVersion: BotMappieVersion,
) => {
    const bot = {
        botId: botMappieVersion.bot_id,
        botVersionId: botMappieVersion.id,
        name: botMappieVersion.name,
        description: botMappieVersion.description,
        language: botMappieVersion.language,
        channel: botMappieVersion.channel,
        smsConfig: botMappieVersion.omnichannel_config?.sms,
        voiceConfig: {
            initialUserResponseTimeout:
                botMappieVersion.omnichannel_config?.voice
                    ?.initial_user_response_timeout ??
                (isChatBot(botMappieVersion?.labels || []) ? 0 : 1800),
            inactivityTimeout: botMappieVersion.omnichannel_config?.voice
                ?.inactivity_timeout
                ? botMappieVersion.omnichannel_config?.voice?.inactivity_timeout
                : 4000,
            ttsVoice: {
                ...botMappieVersion.omnichannel_config?.voice?.tts,
                prosody: undefined,
            },
            stt: {
                provider:
                    botMappieVersion.omnichannel_config?.voice?.stt?.provider,
                endOfSpeechTimeout:
                    botMappieVersion.omnichannel_config?.voice?.stt
                        ?.end_of_speech_timeout ?? 500,
            },
            ttsProsody: {
                rate: prosodyRateToNumber(
                    botMappieVersion.channel,
                    botMappieVersion.omnichannel_config?.voice?.tts?.prosody
                        ?.rate,
                ),
            },
        } as VoiceConfig,
        // omnichannelConfig:
        //     botMappieVersion.omnichannel_config as OmnichannelConfigBot,
        permanent: botMappieVersion.permanent,
        taskValues: taskValuesToArray(botMappieVersion.task_values),

        nodesReactFlow: modulesToNodesReactFlow(
            botMappieVersion.dialog,
            botMappieVersion.ui_config,
        ),
        edgesReactFlow: modulesToEdgesReactFlow(botMappieVersion.dialog),
        prompt_texts: botMappieVersion.dialog?.prompt_texts,
        entity_texts: botMappieVersion.dialog?.entity_texts,
        intent_texts: botMappieVersion.dialog?.intent_texts,
        labels: botMappieVersion.labels,
        stickers: (() => {
            const defaultS1 = {
                S1: {
                    name: 'default',
                    description: '',
                    appearance: {
                        color: '#fceaea',
                    },
                },
            };
            const stickers = botMappieVersion.dialog?.stickers;
            return stickers && Object.keys(stickers).length > 0
                ? stickers
                : defaultS1;
        })(),
        mchannelsBotId: botMappieVersion.mchannels_bot_id,
    };

    return bot as Bot;
};

const prosodyRateToNumber = (
    channel: Channel,
    prosodyRateMappie: string | undefined,
) => {
    let prosodyRate: number | undefined = undefined;

    if (channel === 'voice') {
        const numericProsodyRate = Number(
            prosodyRateMappie?.match(/.*[^%]/)?.[0],
        );

        if (isNaN(numericProsodyRate)) {
            prosodyRate = 100;
        } else {
            prosodyRate = numericProsodyRate;
        }
    }

    return prosodyRate;
};

const taskValuesToArray = (taskValues?: Record<string, string>) => {
    let taskValuesArray = [{ variableName: '', value: '', id: uuid() }];

    if (taskValues) {
        const taskValuesArrayBot = Object.entries(taskValues).map(
            ([key, value]: [string, string]) => {
                return { variableName: key, value: value, id: uuid() };
            },
        );
        if (taskValuesArrayBot.length > 0) {
            taskValuesArray = taskValuesArrayBot;
        }
    }

    return taskValuesArray;
};
